import * as React from 'react';
import { css, StyleSheet } from 'aphrodite';
import { brandPrimary, darkGrayFontColor, destructive, white } from '@Styles/colors';
import { Label } from '@HoudiniUI/Label';
import { Separator } from '@WebComponents/Separator';

const FieldSet = React.forwardRef<HTMLFieldSetElement, AphroditeStyled<React.ComponentProps<'fieldset'>>>(
	({ styles = [], ...props }, ref) => {
		return <fieldset data-slot='field-set' className={css(s.fieldset, ...styles)} ref={ref} {...props} />;
	}
);

FieldSet.displayName = 'FieldSet';

const FieldLegend = React.forwardRef<
	HTMLLegendElement,
	AphroditeStyled<React.ComponentProps<'legend'> & { variant?: 'legend' | 'label' }>
>(({ styles = [], variant = 'legend', ...props }, ref) => {
	return (
		<legend
			data-slot='field-legend'
			data-variant={variant}
			className={css(s.fieldLegend, ...styles)}
			ref={ref}
			{...props}
		/>
	);
});

FieldLegend.displayName = 'FieldLegend';

const FieldGroup = React.forwardRef<HTMLDivElement, AphroditeStyled<React.ComponentProps<'div'>>>(
	({ styles = [], ...props }, ref) => {
		return <div data-slot='field-group' className={css(s.fieldGroup, ...styles)} ref={ref} {...props} />;
	}
);

FieldGroup.displayName = 'FieldGroup';

const Field = React.forwardRef<
	HTMLDivElement,
	AphroditeStyled<React.ComponentProps<'div'> & { orientation?: 'horizontal' | 'vertical' | 'responsive' }>
>(({ styles = [], orientation = 'vertical', ...props }, ref) => {
	return (
		<div
			role='group'
			data-slot='field'
			data-orientation={orientation}
			ref={ref}
			className={css(s.field, ...styles)}
			{...props}
		/>
	);
});

Field.displayName = 'Field';

const FieldContent = React.forwardRef<HTMLDivElement, AphroditeStyled<React.ComponentProps<'div'>>>(
	({ styles = [], ...props }, ref) => {
		return <div data-slot='field-content' ref={ref} className={css(s.fieldContent, ...styles)} {...props} />;
	}
);

FieldContent.displayName = 'FieldContent';

const FieldLabel = React.forwardRef<HTMLLabelElement, React.ComponentProps<typeof Label>>(
	({ styles = [], ...props }, ref) => {
		return <Label data-slot='field-label' ref={ref} styles={[s.fieldLabel, ...styles]} {...props} />;
	}
);

FieldLabel.displayName = 'FieldLabel';

const FieldTitle = React.forwardRef<HTMLDivElement, AphroditeStyled<React.ComponentProps<'div'>>>(
	({ styles = [], ...props }, ref) => {
		return <div data-slot='field-label' ref={ref} className={css(s.fieldTitle, ...styles)} {...props} />;
	}
);

FieldTitle.displayName = 'FieldTitle';

const FieldDescription = React.forwardRef<HTMLParagraphElement, AphroditeStyled<React.ComponentProps<'p'>>>(
	({ styles = [], ...props }, ref) => {
		return <p data-slot='field-description' ref={ref} className={css(s.fieldDescription, ...styles)} {...props} />;
	}
);

FieldDescription.displayName = 'FieldDescription';

const FieldSeparator = React.forwardRef<HTMLDivElement, AphroditeStyled<React.ComponentProps<'div'>>>(
	({ children, styles = [], ...props }, ref) => {
		return (
			<div
				data-slot='field-separator'
				data-content={!!children}
				ref={ref}
				className={css(s.fieldSeparator, ...styles)}
				{...props}
			>
				<Separator styles={[s.separator]} />
				{children && (
					<span className={css(s.fieldSeparatorContent)} data-slot='field-separator-content'>
						{children}
					</span>
				)}
			</div>
		);
	}
);

FieldSeparator.displayName = 'FieldSeparator';

const FieldError = React.forwardRef<
	HTMLDivElement,
	AphroditeStyled<React.ComponentProps<'div'> & { errors?: Array<{ message?: string } | undefined> }>
>(({ styles = [], children, errors, ...props }, ref) => {
	const content = React.useMemo(() => {
		if (children) {
			return children;
		}

		if (!errors?.length) {
			return null;
		}

		const uniqueErrors = [...new Map(errors.map(error => [error?.message, error])).values()];

		if (uniqueErrors?.length === 1) {
			return uniqueErrors[0]?.message;
		}

		return (
			<ul className={css(s.fieldErrorList)}>
				{uniqueErrors.map((error, index) => error?.message && <li key={index}>{error.message}</li>)}
			</ul>
		);
	}, [children, errors]);

	if (!content) {
		return null;
	}

	return (
		<div role='alert' data-slot='field-error' ref={ref} className={css(s.fieldError, ...styles)} {...props}>
			{content}
		</div>
	);
});

FieldError.displayName = 'FieldError';

export {
	Field,
	FieldLabel,
	FieldDescription,
	FieldError,
	FieldGroup,
	FieldLegend,
	FieldSeparator,
	FieldSet,
	FieldContent,
	FieldTitle,
};

const s = StyleSheet.create({
	fieldset: {
		boxSizing: 'border-box',
		border: 'none',
		padding: 0,
		margin: 0,
		minWidth: 0,
		display: 'flex',
		flexDirection: 'column',
		gap: 16,
		':has(> [data-slot="checkbox-group"])': {
			gap: 12,
		},
		':has(> [data-slot="radio-group"])': {
			gap: 12,
		},
	},
	fieldLegend: {
		boxSizing: 'border-box',
		padding: 0,
		margin: 0,
		marginBottom: 6,
		':is([data-variant="label"])': {
			fontSize: 14,
			lineHeight: 1.428,
		},
		':is([data-variant="legend"])': {
			fontSize: 16,
			lineHeight: 1.5,
		},
	},
	fieldGroup: {
		boxSizing: 'border-box',
		containerType: 'inline-size',
		containerName: 'field-group',
		display: 'flex',
		width: '100%',
		flexDirection: 'column',
		gap: 20,
		':is([data-slot="checkbox-group"])': {
			gap: 12,
		},
		':is([data-slot="field-group"] > *)': {
			gap: 16,
		},
	},
	field: {
		boxSizing: 'border-box',
		display: 'flex',
		width: '100%',
		gap: 8,
		':is([data-invalid="true"])': {
			color: destructive,
		},
		':is([data-orientation="vertical"])': {
			flexDirection: 'column',
		},
		':is([data-orientation="vertical"]) > *': {
			width: '100%',
		},
		':is([data-orientation="horizontal"])': {
			flexDirection: 'row',
			alignItems: 'center',
		},
		':is([data-orientation="horizontal"]):has(> [data-slot="field-content"])': {
			alignItems: 'flex-start',
		},
		':is([data-orientation="horizontal"]) > [data-slot="field-label"]': {
			flex: 'auto',
		},
		':is([data-orientation="horizontal"]):has(> [data-slot="field-content"]) > [role="checkbox"]': {
			marginTop: 1,
		},
		':is([data-orientation="horizontal"]):has(> [data-slot="field-content"]) > [role="radio"]': {
			marginTop: 1,
		},
		':is([data-orientation="responsive"])': {
			flexDirection: 'column',
		},
		':is([data-orientation="responsive"]) > *': {
			width: '100%',
		},
		'@container field-group (min-width: 448px)': {
			':is([data-orientation="responsive"])': {
				flexDirection: 'row',
				alignItems: 'center',
			},
			':is([data-orientation="responsive"]) > *': {
				width: 'auto',
			},
			':is([data-orientation="responsive"]):has(> [data-slot="field-content"])': {
				alignItems: 'flex-start',
			},
			':is([data-orientation="responsive"]) > [data-slot="field-label"]': {
				flex: 'auto',
			},
			':is([data-orientation="responsive"]):has(> [data-slot="field-content"]) > [role="checkbox"]': {
				marginTop: 1,
			},
			':is([data-orientation="responsive"]):has(> [data-slot="field-content"]) > [role="radio"]': {
				marginTop: 1,
			},
		},
	},
	fieldContent: {
		boxSizing: 'border-box',
		display: 'flex',
		flex: 1,
		flexDirection: 'column',
		gap: 2,
		lineHeight: 1.375,
	},
	fieldLabel: {
		boxSizing: 'border-box',
		display: 'flex',
		width: 'fit-content',
		gap: 8,
		lineHeight: 1.375,
		borderColor: '#e5e5e5',
		':is([data-slot="field"][data-disabled="true"] *)': {
			opacity: '0.5',
		},
		':has([data-checked])': {
			borderColor: `${brandPrimary}4d`,
			backgroundColor: `${brandPrimary}0d`,
		},
		':has([data-state="checked"])': {
			borderColor: `${brandPrimary}4d`,
			backgroundColor: `${brandPrimary}0d`,
		},
		':has(> [data-slot="field"])': {
			borderRadius: 8,
			borderWidth: 1,
			borderStyle: 'solid',
			width: '100%',
			flexDirection: 'column',
		},
		':is(*) > [data-slot="field"]': {
			padding: 10,
		},
	},
	fieldTitle: {
		display: 'flex',
		width: 'fit-content',
		alignItems: 'center',
		gap: 8,
		fontSize: 14,
		':is([data-slot="field"][data-disabled="true"] *)': {
			opacity: '0.5',
		},
	},
	fieldDescription: {
		boxSizing: 'border-box',
		margin: 0,
		padding: 0,
		textAlign: 'left',
		fontSize: 14,
		lineHeight: 1.5,
		color: darkGrayFontColor,
		':is([data-slot="field"][data-orientation="horizontal"] *)': {
			textWrap: 'balance',
		},
		':is([data-variant="legend"] + *)': {
			marginTop: -6,
		},
		':last-child': {
			marginTop: 0,
		},
		':nth-last-child(2)': {
			marginTop: -4,
		},
		':is(*) > a': {
			textDecoration: 'underline',
			textUnderlineOffset: 4,
		},
		':is(*) > a:hover': {
			color: brandPrimary,
		},
	},
	fieldSeparator: {
		boxSizing: 'border-box',
		position: 'relative',
		marginTop: -8,
		marginBottom: -8,
		height: 20,
		fontSize: 14,
		':is([data-slot="field-group"][data-variant="outline"] *)': {
			marginBottom: -8,
		},
	},
	separator: {
		position: 'absolute',
		inset: 0,
		top: '50%',
	},
	fieldSeparatorContent: {
		boxSizing: 'border-box',
		position: 'relative',
		marginLeft: 'auto',
		marginRight: 'auto',
		display: 'block',
		width: 'fit-content',
		backgroundColor: white,
		paddingLeft: 8,
		paddingRight: 8,
		color: darkGrayFontColor,
	},
	fieldError: {
		boxSizing: 'border-box',
		fontSize: 14,
		color: destructive,
	},
	fieldErrorList: {
		boxSizing: 'border-box',
		marginLeft: 16,
		display: 'flex',
		flexDirection: 'column',
		gap: 4,
		':is(*) > li': {
			listStyleType: 'disc',
		},
	},
});
